/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link as RouterLink, useParams } from 'react-router';

import DeleteIcon from '@mui/icons-material/Delete';
import { Avatar, AvatarGroup, Box, Button, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { notifyReportableApiError } from '../../../../components/notifyReportableApiError';
import { QueryLoader } from '../../../../components/QueryLoader';
import { SelectWithDescription } from '../../../../components/SelectWithDescription/SelectWithDescription';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { TruncatableTypography } from '../../../../components/TruncatableTypography';
import { BasicRoutes } from '../../../../constants/basicRoutes';
import { TeamApiService } from '../../../../services/TeamApiService';
import { UserApiService } from '../../../../services/UserApiService';
import { prepareRoutePath } from '../../../../utils/prepareRoutePath';
import { fetchTeams } from '../../../../utils/queryFns';
import { useOrganizationPermissions } from '../../../../utils/useOrganizationPermissions';
import { useOrganizationRolesI18n } from '../../../../utils/useOrganizationRolesI18n';
import { AddTeamModal } from './AddTeamModal';
import { Team } from './Team';

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) 50px',
    gridAutoRows: 'minmax(100px, 1fr) repeat(minmax(100px, 1fr), 1px)',
    alignItems: 'center',
  },
  cell: {
    height: '100%',
    alignContent: 'center',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  divider: {
    gridColumn: '1 / -1',
  },
  tableHoverEdit: {
    display: 'contents',
    '& > :last-child': {
      visibility: 'hidden',
    },
    '&:hover > :last-child': {
      visibility: 'visible',
    },
  },
}));

export const TeamsTab = memo<Props>(props => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { teamIdentifier } = useParams<{ teamIdentifier: string }>();

  const organizationRoles = useOrganizationRolesI18n();
  const permissions = useOrganizationPermissions(props.organization.id);

  const [addTeamModalOpen, setAddTeamModalOpen] = useState(false);
  const closeTeamModal = () => setAddTeamModalOpen(false);

  const teamsQuery = useQuery([fetchTeams.key, { organizationId: props.organization.id }], fetchTeams.fn);
  const teams = useMemo(
    () => teamsQuery.data?.payload.sort((a, b) => a.displayName.localeCompare(b.displayName)) || [],
    [teamsQuery.data],
  );

  const [teamToBeRemoved, setTeamToBeRemoved] = useState<string | null>(null);
  const removeTeam = useCallback(
    (teamIdentifier: string) =>
      TeamApiService.deleteTeam({ organizationId: props.organization.id, teamIdentifier }).catch(
        notifyReportableApiError,
      ),
    [props.organization.id],
  );

  return teamIdentifier ? (
    <Team organizationId={props.organization.id} teamIdentifier={teamIdentifier} />
  ) : (
    <QueryLoader queries={[teamsQuery]}>
      <Stack>
        <Box className={classes.wrapper}>
          <Stack direction="row" gridColumn="1 / -1" alignItems="center" justifyContent="space-between" pb={1.5}>
            <Typography variant="subtitle2">
              {t('organizationAdmin:teams.table.number_of_teams', { count: teams.length })}
            </Typography>

            {permissions.edit_organization && (
              <Button
                onClick={() => setAddTeamModalOpen(true)}
                data-cy="teams-tab-add-team-button"
                disabled={!permissions.edit_organization}
              >
                {t('organizationAdmin:teams.add_team')}
              </Button>
            )}
          </Stack>

          <Divider className={classes.divider} />

          {teams.length === 0 ? (
            <Box className={classes.cell} style={{ gridColumn: '1 / -1' }}>
              {t('organizationAdmin:teams.table.no_teams')}
            </Box>
          ) : (
            teams.map((team, i) => (
              <>
                <Box className={classes.tableHoverEdit}>
                  <Box className={classes.cell}>
                    <Stack>
                      <RouterLink
                        to={prepareRoutePath(BasicRoutes.MANAGE_TEAM, {
                          ':organization_id': props.organization.id,
                          ':team_identifier': team.identifier,
                        })}
                        style={{
                          textDecoration: 'none',
                          color: 'secondary',
                        }}
                        data-cy={`team-link-${team.identifier}`}
                      >
                        <TruncatableTypography variant="subtitle1">{team.displayName}</TruncatableTypography>
                      </RouterLink>
                      <TruncatableTypography variant="caption">{team.about}</TruncatableTypography>
                    </Stack>
                  </Box>
                  <Box className={classes.cell}>
                    <TruncatableTypography variant="subtitle1">{team.identifier}</TruncatableTypography>
                  </Box>
                  <Box className={classes.cell}>
                    {permissions.edit_organization ? (
                      <SelectWithDescription
                        disableUnderline
                        onSubmit={role =>
                          TeamApiService.patchTeam({
                            organizationId: props.organization.id,
                            teamIdentifier: team.identifier,
                            data: { role: role as (typeof organizationRoles)[number]['key'] },
                          }).catch(notifyReportableApiError)
                        }
                        value={team.role}
                        items={organizationRoles}
                        typographyVariant="subtitle1"
                        data-cy={`teams-${team.identifier}-role`}
                      />
                    ) : (
                      <Typography variant="subtitle1" data-cy={`teams-${team.identifier}-role`}>
                        {team.role}
                      </Typography>
                    )}
                  </Box>
                  <Box className={classes.cell}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle1" component="span">
                        {t('organizationAdmin:teams.table.number_of_members', {
                          count: team.users.length + team.ssoGroups.length,
                        })}
                      </Typography>
                      <AvatarGroup max={5}>
                        {team.users.map(user => (
                          <Tooltip key={user.id} title={`${user.firstName} ${user.lastName} (${user.email})`}>
                            <Avatar
                              src={UserApiService.getUserByIdThumbnailUrl({ userId: user.id })}
                              alt={`${user.firstName} ${user.lastName}`}
                              data-cy={`teams-${team.identifier}-avatar-${user.firstName}-${user.lastName}`}
                            />
                          </Tooltip>
                        ))}
                      </AvatarGroup>
                    </Stack>
                  </Box>
                  <Box className={classes.cell}>
                    {permissions.edit_organization && (
                      <IconButton
                        onClick={() => setTeamToBeRemoved(team.identifier)}
                        size="small"
                        data-cy={`teams-remove-${team.identifier}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {i + 1 !== teams.length ? <Divider className={classes.divider} /> : null}
              </>
            ))
          )}
        </Box>

        <AddTeamModal
          organizationId={props.organization.id}
          open={addTeamModalOpen}
          onCancel={closeTeamModal}
          onSuccess={closeTeamModal}
          existingTeamIdentifiers={teams.map(team => team.identifier)}
        />
        <SimpleConfirmModal
          open={!!teamToBeRemoved}
          message={
            <>
              <Typography>
                {t('organizationAdmin:teams.remove_team_part1')}
                <Typography component="span" style={{ fontWeight: 'bold' }}>
                  {teamToBeRemoved}
                </Typography>
                {t('organizationAdmin:teams.remove_team_part2')}
              </Typography>
              <Typography>{t('organizationAdmin:teams.remove_team_can_not_be_undone')}</Typography>
            </>
          }
          onCancel={() => setTeamToBeRemoved(null)}
          onConfirm={() => removeTeam(teamToBeRemoved!).then(() => setTeamToBeRemoved(null))}
        />
      </Stack>
    </QueryLoader>
  );
});

type Props = {
  organization: {
    id: string;
    name: string;
  };
};
